import React from 'react'
import type { CollectionProduct } from '@lib/types'
import ProductCardSneakPeak from './ProductCardSneakPeak'

interface ProductGridWithPaginationProps {
	products: CollectionProduct[]
	className?: string
}

const PRODUCTS_PER_PAGE = 6

export default function ProductGridWithPagination({
	products,
	className = ''
}: ProductGridWithPaginationProps) {
	const [page, setPage] = React.useState(0)
	const totalPages = Math.ceil(products.length / PRODUCTS_PER_PAGE)

	const startIdx = page * PRODUCTS_PER_PAGE
	const endIdx = startIdx + PRODUCTS_PER_PAGE
	const pageProducts = products.slice(startIdx, endIdx)

	const handlePrev = () => setPage((p) => Math.max(0, p - 1))
	const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1))

	return (
		<div className={`flex flex-col items-center ${className}`}>
			<div className="grid grid-cols-3 gap-x-8 gap-y-10">
				{pageProducts.map((product) => (
					<ProductCardSneakPeak key={product.id} product={product} className="h-[288px]" />
				))}
				{/* Fill empty slots if less than 6 products on last page */}
				{Array.from({ length: Math.max(0, PRODUCTS_PER_PAGE - pageProducts.length) }).map(
					(_, i) => (
						<div key={`empty-${i}`} />
					)
				)}
			</div>
			{/* Paginator always at the bottom */}
			{totalPages > 1 && (
				<div className="flex items-center gap-4 mt-8 justify-center w-full">
					<button
						type="button"
						onClick={handlePrev}
						disabled={page === 0}
						className={`w-12 h-12 flex items-center justify-center border rounded-none transition-colors
						${page === 0 ? 'border-gray-300' : 'border-black'} bg-white`}
						aria-label="Previous page"
					>
						{/* Left arrow */}
						<svg
							width="28"
							height="28"
							viewBox="0 0 28 28"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M17 7L11 14L17 21"
								stroke={page === 0 ? '#BDBDBD' : '#111'}
								strokeWidth="2.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<button
						type="button"
						onClick={handleNext}
						disabled={page === totalPages - 1}
						className={`w-12 h-12 flex items-center justify-center border rounded-none transition-colors
						${page === totalPages - 1 ? 'border-gray-300' : 'border-black'} bg-white`}
						aria-label="Next page"
					>
						{/* Right arrow */}
						<svg
							width="28"
							height="28"
							viewBox="0 0 28 28"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11 7L17 14L11 21"
								stroke={page === totalPages - 1 ? '#BDBDBD' : '#111'}
								strokeWidth="2.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</div>
			)}
		</div>
	)
}
